import React, { useState, useEffect } from 'react';
import { FileText, Send, Link, Music, Youtube, Instagram, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GlassCard = ({ children, className = "" }) => (
  <div className={`backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 ${className}`}>
    {children}
  </div>
);

const SubmitArticle = () => {
  const [form, setForm] = useState({
    title: '',
    content: '',
    image_url: '',
    category: 'community',
    tags: '',
    spotify_embed: '',
    youtube_embed: '',
    instagram_url: '',
    soundcloud_url: '',
    apple_music_url: '',
    submitter_name: '',
    submitter_email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionsEnabled, setSubmissionsEnabled] = useState(true);

  const categories = [
    { value: 'community', label: 'Community News' },
    { value: 'artist-spotlight', label: 'Artist Spotlight' },
    { value: 'album-review', label: 'Album Review' },
    { value: 'event-recap', label: 'Event Recap' },
    { value: 'production-tips', label: 'Production Tips' },
    { value: 'industry-news', label: 'Industry News' },
    { value: 'opinion', label: 'Opinion / Editorial' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchSubmissionsConfig();
  }, []);

  const fetchSubmissionsConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/submissions/config`);
      setSubmissionsEnabled(res.data.articles_submissions_enabled);
    } catch (error) {
      console.error('Failed to fetch submissions config:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content || !form.submitter_name || !form.submitter_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : []
      };
      await axios.post(`${API}/api/submit/article`, submitData);
      toast.success('Article submitted for review!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit article');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-white/20 text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 transition-colors";
  const labelClass = "block text-sm font-medium mb-2 text-gray-300";

  if (!submissionsEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <GlassCard className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Submissions Closed</h3>
            <p className="text-gray-400">Article submissions are currently not being accepted. Check back later!</p>
          </GlassCard>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <GlassCard className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Article Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for your submission. Our team will review it and get back to you soon.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  title: '', content: '', image_url: '', category: 'community', tags: '',
                  spotify_embed: '', youtube_embed: '', instagram_url: '', soundcloud_url: '',
                  apple_music_url: '', submitter_name: '', submitter_email: ''
                });
              }}
              className="bg-purple-500 hover:bg-purple-400 text-white"
            >
              Submit Another Article
            </Button>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 bg-clip-text text-transparent">
              SUBMIT ARTICLE
            </span>
          </h1>
          <p className="text-gray-400 text-lg">
            Share your story, review, or news with the BeatLab52 community
          </p>
        </div>

        <GlassCard>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Submitter Info */}
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <h3 className="font-bold text-purple-400 mb-4">Your Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Your Name *</label>
                  <input
                    type="text"
                    value={form.submitter_name}
                    onChange={(e) => setForm(f => ({...f, submitter_name: e.target.value}))}
                    placeholder="Your name"
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Your Email *</label>
                  <input
                    type="email"
                    value={form.submitter_email}
                    onChange={(e) => setForm(f => ({...f, submitter_email: e.target.value}))}
                    placeholder="your@email.com"
                    className={inputClass}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Article Content */}
            <div>
              <label className={labelClass}>Article Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
                placeholder="Give your article a catchy title"
                className={inputClass}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(f => ({...f, category: e.target.value}))}
                  className={inputClass}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value} className="bg-black">{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Tags (comma separated)</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm(f => ({...f, tags: e.target.value}))}
                  placeholder="hip-hop, review, news"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Article Content *</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm(f => ({...f, content: e.target.value}))}
                placeholder="Write your article here. Use line breaks for paragraphs. You can use emojis and formatting like bullet points (•)..."
                className={`${inputClass} h-64 py-3`}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: Use emojis, bullet points (•), and line breaks to make your article engaging!
              </p>
            </div>

            <div>
              <label className={labelClass}>Featured Image URL</label>
              <input
                type="url"
                value={form.image_url}
                onChange={(e) => setForm(f => ({...f, image_url: e.target.value}))}
                placeholder="https://... (paste image URL)"
                className={inputClass}
              />
            </div>

            {/* Supporting Links */}
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <Link className="w-4 h-4" />
                Supporting Links (Optional)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`${labelClass} flex items-center gap-2`}>
                    <Music className="w-4 h-4 text-green-500" /> Spotify Embed
                  </label>
                  <input
                    type="url"
                    value={form.spotify_embed}
                    onChange={(e) => setForm(f => ({...f, spotify_embed: e.target.value}))}
                    placeholder="https://open.spotify.com/embed/..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={`${labelClass} flex items-center gap-2`}>
                    <Youtube className="w-4 h-4 text-red-500" /> YouTube Embed
                  </label>
                  <input
                    type="url"
                    value={form.youtube_embed}
                    onChange={(e) => setForm(f => ({...f, youtube_embed: e.target.value}))}
                    placeholder="https://youtube.com/embed/..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={`${labelClass} flex items-center gap-2`}>
                    <Instagram className="w-4 h-4 text-pink-500" /> Instagram URL
                  </label>
                  <input
                    type="url"
                    value={form.instagram_url}
                    onChange={(e) => setForm(f => ({...f, instagram_url: e.target.value}))}
                    placeholder="https://instagram.com/..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>SoundCloud URL</label>
                  <input
                    type="url"
                    value={form.soundcloud_url}
                    onChange={(e) => setForm(f => ({...f, soundcloud_url: e.target.value}))}
                    placeholder="https://soundcloud.com/..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Apple Music URL</label>
                  <input
                    type="url"
                    value={form.apple_music_url}
                    onChange={(e) => setForm(f => ({...f, apple_music_url: e.target.value}))}
                    placeholder="https://music.apple.com/..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-bold text-lg"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Submit Article for Review
                  </span>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-3">
                Your article will be reviewed by our team before publishing
              </p>
            </div>
          </form>
        </GlassCard>
      </div>
    </div>
  );
};

export default SubmitArticle;
